import { AnimatePresence, motion } from "framer-motion";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./TextEditor.css";

import AIPromptModal from "./AIPromptModal";
import CommentSection from "./CommentSection";
import DocumentSidebar from "./DocumentSidebar";
import EditorContent from "./EditorContent";
import ShareDocument from "./ShareDocument";
import TopBar from "./TopBar";
import TableOfContents from "./TableOfContents";
import FindReplace from "./FindReplace";
import WordCountReadingTime from "./WordCountReadingTime";
import CommentsAnnotations from "./CommentsAnnotations";

const SAVE_INTERVAL_MS = 3000;

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ font: [] }],
  [{ size: ["small", false, "large", "huge"] }],
  [{ color: [] }, { background: [] }],
  [{ bold: true }, { italic: true }, { underline: true }],
  [{ strike: true }],
  [{ script: "sub" }, { script: "super" }],
  [{ blockquote: true }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ indent: "-1" }, { indent: "+1" }],
  [{ direction: "rtl" }],
  [{ align: [] }],
  ["link"],
  ["image", "video"],
  ["clean"],
  ["code-block"],
  ["formula"],
  ["custom-style"],
  ["table"],
  ["footnote"],
  ["citation"],
];

function TextEditor() {
  const { id: documentId } = useParams();
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const [socket, setSocket] = useState(null);
  const [quill, setQuill] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState(documentId);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [hasActiveDocument, setHasActiveDocument] = useState(
    Boolean(documentId)
  );

  useEffect(() => {
    if (!authState?.token) {
      navigate("/login");
    }
  }, [authState, navigate]);

  useEffect(() => {
    const s = io(`${process.env.REACT_APP_BACKEND_URL}`);
    setSocket(s);

    s.on("connect", () => console.log("Socket connected"));

    return () => {
      s.disconnect();
    };
  }, []);

  const loadDocumentAndAnnotations = useCallback(async () => {
    if (!activeDocumentId || !quill || !authState.token) return;

    try {
      const [documentResponse, annotationsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${activeDocumentId}`, {
          headers: { "x-auth-token": authState.token },
        }),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${activeDocumentId}/annotations`,
          {
            headers: { "x-auth-token": authState.token },
          }
        ),
      ]);

      const { data: content } = documentResponse.data;
      const fetchedAnnotations = annotationsResponse.data;

      quill.setContents(content);
      setAnnotations(fetchedAnnotations);
      quill.enable();
    } catch (error) {
      console.error("Error loading document and annotations:", error);
    }
  }, [activeDocumentId, quill, authState.token]);

  useEffect(() => {
    if (socket == null || quill == null || !activeDocumentId) return;

    socket.emit("get-document", activeDocumentId);

    socket.once("load-document", (document) => {
      quill.setContents(document);
      quill.enable();
    });

    loadDocumentAndAnnotations();
  }, [socket, quill, activeDocumentId, loadDocumentAndAnnotations]);

  useEffect(() => {
    if (socket == null || quill == null) return;

    const handler = (delta) => {
      quill.updateContents(delta);
    };

    socket.on("receive-changes", handler);

    return () => {
      socket.off("receive-changes", handler);
    };
  }, [socket, quill]);

  useEffect(() => {
    if (socket == null || quill == null) return;

    const handler = (delta, oldDelta, source) => {
      if (source !== "user") return;
      socket.emit("send-changes", delta);
    };

    quill.on("text-change", handler);

    return () => {
      quill.off("text-change", handler);
    };
  }, [socket, quill]);

  useEffect(() => {
    if (socket == null || quill == null) return;

    const interval = setInterval(() => {
      socket.emit("save-document", quill.getContents());
    }, SAVE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [socket, quill]);

  const wrapperRef = useCallback((wrapper) => {
    if (!wrapper) return;

    wrapper.innerHTML = "";
    const editor = document.createElement("div");
    wrapper.append(editor);
    const q = new Quill(editor, {
      theme: "snow",
      modules: {
        toolbar: TOOLBAR_OPTIONS,
        clipboard: {
          matchVisual: false,
        },
      },
    });
    q.disable();
    q.setText("Loading...");
    setQuill(q);
  }, []);

  const handleAnnotationsUpdate = useCallback(
    async (updatedAnnotations) => {
      setAnnotations(updatedAnnotations);
      try {
        await axios.post(
         `${process.env.REACT_APP_BACKEND_URL}/api/documents/${activeDocumentId}/annotations`,
          {
            annotations: updatedAnnotations,
          },
          {
            headers: { "x-auth-token": authState.token },
          }
        );
      } catch (error) {
        console.error("Error saving annotations:", error);
      }
    },
    [activeDocumentId, authState.token]
  );

  return (
    <div className="flex h-screen mt-16 bg-gray-100 text-gray-800 font-sans">
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          setIsSidebarOpen={setIsSidebarOpen}
          isSidebarOpen={isSidebarOpen}
          setShowPromptInput={setShowPromptInput}
          quill={quill}
        />

        <div className="flex-1 flex overflow-hidden">
          <AnimatePresence>
            {isSidebarOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 300, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="bg-white shadow-lg z-20 overflow-hidden"
              >
                <DocumentSidebar
                  activeDocumentId={activeDocumentId}
                  setActiveDocumentId={setActiveDocumentId}
                  setHasActiveDocument={setHasActiveDocument}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex-1 flex flex-col overflow-hidden">
            {hasActiveDocument ? (
              <EditorContent wrapperRef={wrapperRef} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-xl text-gray-500">
                  Select a document or create a new one to start editing
                </p>
              </div>
            )}
          </div>

          <nav className="hidden lg:block w-80 bg-gray-50 overflow-y-auto">
            <div className="px-2 py-6 space-y-6">
              <TableOfContents quill={quill} />
              <FindReplace quill={quill} />
              <WordCountReadingTime quill={quill} />
              <CommentsAnnotations
                quill={quill}
                annotations={annotations}
                onAnnotationsUpdate={handleAnnotationsUpdate}
                documentId={activeDocumentId}
              />
              <CommentSection documentId={activeDocumentId} />
              <ShareDocument documentId={activeDocumentId} />
            </div>
          </nav>
        </div>
      </div>

      {showPromptInput && (
        <AIPromptModal setShowPromptInput={setShowPromptInput} quill={quill} />
      )}
    </div>
  );
}

export default TextEditor;
